import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    intro: "This Privacy Policy explains how Certify IP Global collects, uses, and protects information submitted through this website.",
    sections: [
      {
        heading: "Information We Collect",
        body: [
          "When you submit a consultation request, we collect the information you provide: your name, company, work email address, phone number, the service you are interested in, the approximate number of records involved, and your message.",
          "We may also collect standard technical information — such as browser type and pages visited — through routine website analytics to help us improve the site.",
        ],
      },
      {
        heading: "How We Use Your Information",
        body: [
          "We use submitted information solely to respond to your enquiry, assess your validation requirements, and communicate with you about our services. We do not sell, rent, or trade your personal information to third parties.",
        ],
      },
      {
        heading: "Confidentiality of Client Data",
        body: [
          "Patent portfolio data, records, and related materials shared with us during an engagement are treated confidentially and handled under controlled, documented processes. Access is limited to what is required to perform the agreed validation work.",
        ],
      },
      {
        heading: "Data Retention",
        body: [
          "We retain enquiry information only for as long as needed to respond to your request and maintain appropriate business records. You may request deletion of your information at any time.",
        ],
      },
      {
        heading: "Sharing With Service Providers",
        body: [
          "We use a limited number of trusted service providers (such as website hosting and transactional email delivery) to operate this site. These providers process information only as needed to deliver their services to us.",
        ],
      },
      {
        heading: "Security",
        body: [
          "We apply controlled data handling and quality-control procedures to protect information in our care. No method of transmission or storage is completely secure, and we encourage you to avoid sending highly sensitive materials through the website form.",
        ],
      },
      {
        heading: "Your Rights",
        body: [
          "You may request access to, correction of, or deletion of the personal information you have submitted by contacting us at the email address below.",
        ],
      },
      {
        heading: "Contact",
        body: [
          "Questions about this policy: info@certifyipglobal.com.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    intro: "These Terms of Use govern your use of the Certify IP Global website. By using this site, you accept these terms.",
    sections: [
      {
        heading: "Description of Services",
        body: [
          "Certify IP Global provides intellectual property data validation, patent information verification, and administrative support services for law firms, corporate IP departments, and IP operations teams.",
        ],
      },
      {
        heading: "No Legal Advice",
        body: [
          "Certify IP Global is not a law firm. Nothing on this website or in our services constitutes legal advice, legal representation, or attorney review. Our services are administrative and data-quality in nature.",
        ],
      },
      {
        heading: "No Attorney-Client Relationship",
        body: [
          "Use of this website, submission of the consultation form, or engagement of our services does not create an attorney-client relationship, and communications with us are not protected by attorney-client privilege.",
        ],
      },
      {
        heading: "No Guarantee of Outcomes",
        body: [
          "While we apply structured validation workflows and quality-control procedures, we do not guarantee the completeness or accuracy of any source patent data, that any deadline will be met, or that any patent right will be preserved. Patent deadlines and legal rights should be independently confirmed by the responsible patent attorney, law firm, or authorized IP professional.",
        ],
      },
      {
        heading: "Use of the Website",
        body: [
          "You agree to use this website only for lawful purposes and to provide accurate information when submitting requests. Content on this site is provided for general informational purposes.",
        ],
      },
      {
        heading: "Intellectual Property",
        body: [
          "All content, branding, and design on this website are the property of Certify IP Global and may not be reproduced without permission.",
        ],
      },
      {
        heading: "Limitation of Liability",
        body: [
          "To the fullest extent permitted by law, Certify IP Global is not liable for indirect, incidental, or consequential damages arising from use of this website or reliance on its content.",
        ],
      },
      {
        heading: "Changes to These Terms",
        body: [
          "We may update these terms from time to time. The current version will always be available on this page.",
        ],
      },
      {
        heading: "Contact",
        body: [
          "Questions about these terms: info@certifyipglobal.com.",
        ],
      },
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    intro: "Please read this disclaimer carefully before using our website or services.",
    sections: [
      {
        heading: "Service Disclaimer",
        body: [
          "Certify IP Global provides intellectual property data validation, information verification, and administrative support services. Our services are not a substitute for legal advice, legal representation, or attorney review. Patent deadlines and legal rights should be independently confirmed by the responsible patent attorney, law firm, or authorized IP professional.",
        ],
      },
      {
        heading: "Not a Law Firm",
        body: [
          "Certify IP Global is not a law firm and does not provide legal advice or legal representation. Nothing on this website should be interpreted as legal opinion or counsel.",
        ],
      },
      {
        heading: "No Guaranteed Deadlines or Outcomes",
        body: [
          "We do not guarantee that any patent deadline will be met, that any deadline information is free from error, or that any patent right will be preserved. Validation results highlight information requiring further professional review and should be verified by qualified counsel.",
        ],
      },
      {
        heading: "No Attorney-Client Privilege",
        body: [
          "Communications with Certify IP Global are not protected by attorney-client privilege. Do not submit information through this website that requires privileged treatment.",
        ],
      },
      {
        heading: "Informational Purposes",
        body: [
          "All website content is provided for general informational purposes only and may be updated without notice.",
        ],
      },
      {
        heading: "Contact",
        body: [
          "Questions about this disclaimer: info@certifyipglobal.com.",
        ],
      },
    ],
  },
};

export default function LegalPage({ page }) {
  const content = CONTENT[page] || CONTENT.privacy;

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    document.title = `${content.title} | Certify IP Global`;
    return () => {
      document.title = "US Patent Data Validation & Deadline Verification | Certify IP Global";
    };
  }, [content.title]);

  return (
    <main data-testid={`legal-page-${page}`} className="bg-white min-h-screen">
      <header className="bg-navy border-b border-navy-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="legal-logo-link" className="flex items-center gap-3">
            <span className="w-8 h-8 bg-white/10 border border-white/15 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="#2563EB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <span className="font-display font-bold text-lg tracking-tight text-white">Certify IP Global</span>
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-300 hover:text-white transition-colors duration-300"
          >
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-20 lg:py-28">
        <Reveal>
          <p className="font-monodata text-[10px] tracking-[0.3em] uppercase text-accent mb-5">
            Certify IP Global — Legal
          </p>
          <h1 className="font-display font-bold text-navy text-3xl sm:text-4xl lg:text-5xl tracking-tighter mb-4" data-testid={`legal-${page}-title`}>
            {content.title}
          </h1>
          <p className="font-monodata text-xs text-slate-400 mb-6">Last updated: August 2026</p>
          <p className="text-base text-slate-600 leading-relaxed mb-14 border-l-2 border-accent pl-6">
            {content.intro}
          </p>
          <div className="space-y-10">
            {content.sections.map((s, i) => (
              <section key={s.heading} data-testid={`legal-section-${i + 1}`}>
                <h2 className="font-display font-bold text-navy text-xl tracking-tight mb-3">
                  <span className="font-monodata text-xs text-accent mr-3">{String(i + 1).padStart(2, "0")}</span>
                  {s.heading}
                </h2>
                {s.body.map((p) => (
                  <p key={p.slice(0, 32)} className="text-[15px] text-slate-600 leading-relaxed mb-3">
                    {p}
                  </p>
                ))}
              </section>
            ))}
          </div>
        </Reveal>
      </article>

      <Footer />
    </main>
  );
}
